import api from './api';

/**
 * Convert a base64url VAPID public key to Uint8Array for the browser push API.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Wait until the service worker is registered and active (ready).
 */
export async function getSWRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // If already registered, this returns it; otherwise register.
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) return await navigator.serviceWorker.ready;
    await navigator.serviceWorker.register('/service-worker.js');
    return await navigator.serviceWorker.ready;
  } catch (e) {
    return null;
  }
}

async function fetchVapidKey() {
  const { data } = await api.get('/push/public-key');
  return data.key;
}

function subToPayload(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: navigator.userAgent.slice(0, 300),
  };
}

/**
 * Subscribe to Web Push. Returns { ok: bool, reason?: string, error?: string }.
 * MUST be called from a user gesture (button click) on iOS/Safari, otherwise
 * the permission request is blocked silently.
 */
export async function enablePush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  let reg;
  try {
    reg = await getSWRegistration();
  } catch (e) {
    return { ok: false, reason: 'sw-failed', error: String(e?.message || e) };
  }
  if (!reg) return { ok: false, reason: 'sw-failed' };

  let perm = Notification.permission;
  if (perm === 'default') {
    try {
      perm = await Notification.requestPermission();
    } catch (e) {
      return { ok: false, reason: 'permission-error', error: String(e?.message || e) };
    }
  }
  if (perm !== 'granted') return { ok: false, reason: 'denied' };

  let key;
  try {
    key = await fetchVapidKey();
  } catch (e) {
    return { ok: false, reason: 'no-vapid-key', error: String(e?.message || e) };
  }
  if (!key) return { ok: false, reason: 'no-vapid-key' };

  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
  } catch (e) {
    return { ok: false, reason: 'subscribe-failed', error: String(e?.message || e) };
  }

  try {
    await api.post('/push/subscribe', subToPayload(sub));
  } catch (e) {
    return { ok: false, reason: 'server-save-failed', error: String(e?.message || e) };
  }
  return { ok: true };
}

export async function disablePush() {
  if (!isPushSupported()) return { ok: true };
  const reg = await getSWRegistration();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.post('/push/unsubscribe', subToPayload(sub)); }
    catch (e) { console.warn('[push] unsubscribe(server) failed:', e?.message || e); }
    try { await sub.unsubscribe(); }
    catch (e) { console.warn('[push] unsubscribe(browser) failed:', e?.message || e); }
  }
  // Also persist the preference on the server
  try { await api.put('/push/settings', { enabled: false }); }
  catch (e) { console.warn('[push] settings save failed:', e?.message || e); }
  return { ok: true };
}

/**
 * Get current status: browser permission + backend enabled flag + local subscription
 */
export async function getPushState() {
  const supported = isPushSupported();
  if (!supported) return { supported: false, permission: 'unsupported', subscribed: false, enabled: false };
  const reg = await getSWRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  let enabled = true;
  try {
    const { data } = await api.get('/push/status');
    enabled = !!data.enabled;
  } catch (e) { console.warn('[push] getPushState:', e?.message || e); }
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!sub,
    enabled,
  };
}

/**
 * DEPRECATED: on iOS/Safari, calling requestPermission() outside a user gesture
 * is silently blocked and the user is then unable to be prompted again in the
 * same session. We now rely exclusively on the persistent Home banner (button
 * click = user gesture).
 */
export async function autoPromptOnFirstLogin() {
  // no-op — kept for backward compatibility with existing callers
}

/**
 * Listen for the SW asking us to re-subscribe (browser key rotation).
 */
export function bindResubscribeListener() {
  if (!('serviceWorker' in navigator)) return () => {};
  const handler = (event) => {
    if (event.data && event.data.type === 'resubscribe-push') {
      enablePush().catch(() => {});
    }
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
